/**
 * BestHeightManager
 *
 * Manages best height records for players who haven't completed the level yet
 * - Stores best height reached per username
 * - Only one record per username (updates when beaten)
 * - Displayed separately from completed runs on leaderboard
 */

export interface BestHeightRecord {
  username: string;
  bestHeight: number; // in meters
  timestamp: number;
  date: string;
}

export class BestHeightManager {
  private static readonly STORAGE_KEY = 'blobo-best-heights';

  /**
   * Update best height for a username (only if better than previous)
   */
  static updateBestHeight(username: string, height: number): boolean {
    const records = this.getAllRecords();

    // Find existing record for this username
    const existingIndex = records.findIndex(r => r.username === username);

    if (existingIndex >= 0) {
      const existingRecord = records[existingIndex];
      if (!existingRecord) return false;

      // Only update if new height is better
      if (height > existingRecord.bestHeight) {
        records[existingIndex] = {
          username,
          bestHeight: height,
          timestamp: Date.now(),
          date: new Date().toLocaleDateString(),
        };
        this.saveRecords(records);
        return true; // Updated
      }
      return false; // Not better
    } else {
      // New record for this username
      records.push({
        username,
        bestHeight: height,
        timestamp: Date.now(),
        date: new Date().toLocaleDateString(),
      });
      this.saveRecords(records);
      return true; // New record
    }
  }

  /**
   * Remove best height record for a username (called when they complete the level)
   */
  static removeRecord(username: string): void {
    const records = this.getAllRecords();
    const filtered = records.filter(r => r.username !== username);
    this.saveRecords(filtered);
  }

  /**
   * Get all best height records sorted by height (highest first)
   */
  static getAllRecords(): BestHeightRecord[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];

      const records = JSON.parse(data) as BestHeightRecord[];

      // Sort by best height (highest first)
      return records.sort((a, b) => b.bestHeight - a.bestHeight);
    } catch (error) {
      console.error('Failed to load best heights:', error);
      return [];
    }
  }

  /**
   * Get best height for specific username
   */
  static getBestHeight(username: string): number | null {
    const records = this.getAllRecords();
    const record = records.find(r => r.username === username);
    return record ? record.bestHeight : null;
  }

  /**
   * Get top N records
   */
  static getTopRecords(limit: number = 10): BestHeightRecord[] {
    const records = this.getAllRecords();
    return records.slice(0, limit);
  }

  /**
   * Update username for all records (when player changes their name)
   */
  static updateUsername(oldUsername: string, newUsername: string): void {
    const records = this.getAllRecords();
    let updated = false;

    records.forEach(record => {
      if (record.username === oldUsername) {
        record.username = newUsername;
        updated = true;
      }
    });

    if (updated) {
      this.saveRecords(records);
    }
  }

  /**
   * Clear all best height records
   */
  static clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Save records to localStorage
   */
  private static saveRecords(records: BestHeightRecord[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(records));
    } catch (error) {
      console.error('Failed to save best heights:', error);
    }
  }
}
